'use client';

import { createElement, useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { Eye, Download } from 'lucide-react';
import { getFileIcon, getFileIconColor, getThumbnailUrl } from '../utils';
import type { ThumbnailSize } from '../types';

interface DocumentThumbnailProps {
  documentId: number;
  documentName: string;
  fileType: string;
  hasThumbnail: boolean;
  size?: ThumbnailSize;
  showHoverPreview?: boolean;
  fillHeight?: boolean;
  onView?: () => void;
  onDownload?: () => void;
}

/**
 * DocumentThumbnail - A4 ratio document preview with optional hover actions
 *
 * Uses the backend thumbnail endpoint when available, falls back to file type icon.
 * Maintains A4 aspect ratio (1:1.414) for consistent document preview appearance.
 */
export function DocumentThumbnail({
  documentId,
  documentName,
  fileType,
  hasThumbnail,
  size = 'medium',
  showHoverPreview = false,
  fillHeight = false,
  onView,
  onDownload
}: DocumentThumbnailProps) {
  const [imageError, setImageError] = useState(false);

  const sizeClasses = {
    small: 'w-12 h-16', // 48x64 - A4 ratio
    medium: 'w-16 h-[86px]', // 64x86 - A4 ratio
    large: 'w-20 h-[106px]', // 80x106 - A4 ratio
    wide: 'w-60 h-20' // eg. signature preview
  };

  const iconComponent = getFileIcon(fileType);
  const iconColorClass = getFileIconColor(fileType);
  const showThumbnail = hasThumbnail && !imageError;
  const thumbnailUrl = getThumbnailUrl(documentId);

  return (
    <div
      className={cn(
        'group/thumb bg-card relative flex-shrink-0 overflow-hidden',
        fillHeight ? 'w-20' : sizeClasses[size],
        !fillHeight && 'border-border rounded-md border shadow-sm'
      )}
    >
      {showThumbnail ? (
        <div className="bg-card flex h-full w-full items-center justify-center">
          <Image
            src={thumbnailUrl}
            alt={documentName}
            fill
            className="object-contain"
            onError={() => setImageError(true)}
            unoptimized // Backend serves pre-generated thumbnails
          />
        </div>
      ) : (
        <div className="bg-surface-subtle flex h-full w-full items-center justify-center">
          {createElement(iconComponent, {
            className: cn('h-5 w-5', iconColorClass)
          })}
        </div>
      )}

      {/* Hover overlay with View and Download actions */}
      {showHoverPreview && (onView || onDownload) && (
        <div className="bg-scrim/40 absolute inset-0 flex flex-col items-center justify-center gap-1 opacity-0 transition-opacity group-hover/thumb:opacity-100">
          {onView && (
            <button
              onClick={onView}
              className="bg-card/90 hover:bg-card flex h-7 w-7 cursor-pointer items-center justify-center rounded-full shadow-sm transition-colors"
            >
              <Eye className="text-foreground-secondary h-3.5 w-3.5" />
            </button>
          )}
          {onDownload && (
            <button
              onClick={onDownload}
              className="bg-card/90 hover:bg-card flex h-7 w-7 cursor-pointer items-center justify-center rounded-full shadow-sm transition-colors"
            >
              <Download className="text-foreground-secondary h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
